import { createFileRoute, Link } from "@tanstack/react-router";
import { ServicePageLayout } from "@/components/service/ServicePageLayout";
import { SEARCH_TERM_SECTIONS } from "@/lib/search-term-sections";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";

const SLUG = "/tile-roof-repair-phoenix";
const NAME = "Tile Roof Repair";
const TITLE = "Tile Roof Repair in Phoenix | Durafoam Roofing";
const DESCRIPTION =
  "Tile roof repair in Phoenix — cracked and slipped tile, valley and flashing leaks, underlayment repair, profile matching. Free inspection. Family-owned since 1989.";

const A = "font-semibold text-primary underline underline-offset-2 hover:text-accent";

const faqs = [
  {
    q: "How much does tile roof repair cost in Phoenix?",
    a: "It depends entirely on what failed. Replacing a handful of cracked tiles is a short service call. Reflashing a valley, rebuilding a headwall, or replacing underlayment in a leaking section is a bigger job because the tile has to come off and go back on. Most single-area tile repairs land in the hundreds to low thousands. We give you an exact written number after a free inspection — see our tile roof cost guide for full ranges.",
    aNode: (
      <>
        It depends entirely on what failed. Replacing a handful of cracked tiles is a short service
        call. Reflashing a valley, rebuilding a headwall, or replacing underlayment in a leaking
        section is a bigger job because the tile has to come off and go back on. Most single-area
        tile repairs land in the hundreds to low thousands. We give you an exact written number after
        a free inspection — see our{" "}
        <Link to="/tile-roof-replacement-cost-in-phoenix" className={A}>
          Phoenix tile roof cost guide
        </Link>{" "}
        for full ranges.
      </>
    ),
  },
  {
    q: "Why does my tile roof leak when no tiles are broken?",
    a: "Because the tile isn't what waterproofs your roof — the underlayment beneath it is. Tile sheds most of the water and shields the underlayment from the sun; the felt or synthetic membrane under the tile is what actually keeps water out. In Phoenix that underlayment typically reaches end of life at 20–25 years, so a roof with perfect-looking tile can leak steadily. This is the single most common tile leak we're called out for.",
  },
  {
    q: "Can you replace just one broken tile?",
    a: "Yes, and we do it constantly. As long as the underlayment below is still sound, cracked or slipped tiles are a single-tile repair — we lift the surrounding tiles, swap the broken one, and reset the course. We stock and source the common Phoenix concrete and clay profiles so the repair blends in.",
  },
  {
    q: "Can you match my tile if the profile is discontinued?",
    a: "Usually. Between our own stock, salvage yards, and manufacturer cross-references, we can match most Valley profiles from the 1980s onward. When a match truly doesn't exist, we relocate original tile to the visible slopes and place the closest match where nobody sees it — a trick that keeps the roof looking right from the street.",
  },
  {
    q: "Should I repair the tile or replace the underlayment?",
    a: "It comes down to where you are in the underlayment's life. If the roof is under about 15 years old and the leak is localized, repair it. If the underlayment is 20+ years old and you've had leaks in more than one place, spot repairs become a treadmill — replacing underlayment and re-installing your existing tile is the more economical answer. We tell you which case you're in, with photos, and we don't pretend a 25-year-old roof is a patch job.",
  },
  {
    q: "How long does a tile roof repair take?",
    a: "Most tile repairs are same-day. Broken tiles and a flashing reseal are typically a few hours. A valley rebuild or an underlayment repair in one section usually runs one to two days depending on how much tile has to be removed and stacked.",
  },
  {
    q: "How long does a tile roof last in Phoenix?",
    a: "The tile itself easily lasts 50+ years — concrete and clay barely care about our climate. The underlayment underneath is the wear item, and Phoenix heat generally gets 20–25 years out of it. That's why most Valley 'tile roof replacements' are really underlayment replacements with the original tile reused.",
  },
  {
    q: "Is it safe to walk on my tile roof to look for damage?",
    a: "Please don't. Tile is strong in the middle and fragile at the edges, and a good share of the leak calls we get on tile roofs start with a homeowner, satellite installer, or HVAC tech cracking tile they were standing on. We'll do the walking as part of a free inspection.",
  },
  {
    q: "Do you repair tile roofs damaged by monsoon storms?",
    a: "Yes. Wind-lifted and shifted tile, debris breakage, and blown-off ridge caps are all common after a monsoon. We document the damage with photos so it's usable for an insurance claim, and we tarp and stabilize first if water is still getting in.",
    aNode: (
      <>
        Yes. Wind-lifted and shifted tile, debris breakage, and blown-off ridge caps are all common
        after a monsoon. We document the damage with photos so it's usable for an insurance claim —
        see{" "}
        <Link to="/storm-damage-repair" className={A}>
          storm &amp; hail damage repair
        </Link>{" "}
        — and we tarp and stabilize first if water is still getting in.
      </>
    ),
  },
];

export const Route = createFileRoute("/tile-roof-repair-phoenix")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) =>
    serviceHead({
      override: loaderData?.seo,
      slug: SLUG,
      title: TITLE,
      description: DESCRIPTION,
      serviceName: NAME,
      faqs,
      rating: loaderData?.rating,
      reviewCount: loaderData?.reviewCount,
    }),
  component: Page,
});

function Page() {
  return (
    <ServicePageLayout
      pageSlug="tile-roof-repair-phoenix"
      extraSections={SEARCH_TERM_SECTIONS["tile-roof-repair-phoenix"]}
      eyebrow="Tile Roof Repair"
      h1="Tile Roof Repair in Phoenix"
      subhead="Cracked and slipped tile, leaking valleys and flashings, and failed underlayment under good tile — repaired by a Valley tile crew that's been doing this since 1989."
      heroBullets={[
        "Cracked, slipped & broken tile replaced",
        "Concrete and clay profile matching",
        "Valley, flashing & underlayment repair",
        "Free inspection with photos",
      ]}
      whatIs={{
        heading: "What actually breaks on a Phoenix tile roof",
        body: (
          <>
            <p>
              Here's the thing most homeowners are never told: <strong>the tile is not what keeps
              water out of your house.</strong> Tile is a shedding surface and a sunshade. The
              waterproofing is the underlayment — the felt or synthetic membrane rolled onto the deck
              beneath the tile. Understand that, and every tile roof problem in Phoenix makes sense.
            </p>
            <ul>
              <li>
                <strong>Cracked or broken tile.</strong> Almost always foot traffic — HVAC service,
                satellite installs, solar techs, or a homeowner going up to look at something. Broken
                tile exposes the underlayment to direct UV, which shortens its life fast.
              </li>
              <li>
                <strong>Slipped tile.</strong> Tiles walk downhill over decades as the fasteners and
                mortar loosen. A slipped course leaves a gap that drives water sideways instead of
                down.
              </li>
              <li>
                <strong>Valley failures.</strong> Valleys carry the most water on the roof. Old
                galvanized valley metal rusts through at the bottom, and debris packs the channel
                until water backs up under the tile.
              </li>
              <li>
                <strong>Flashing and headwall leaks.</strong> Where the roof meets a wall, chimney,
                parapet, or skylight curb. Sealants dry out in our sun on roughly a 7–10 year cycle.
              </li>
              <li>
                <strong>Failed underlayment.</strong> The big one. At 20–25 years the membrane
                becomes brittle and no longer seals around fasteners — so the roof leaks even though
                the tile looks perfect from the driveway.
              </li>
              <li>
                <strong>Ridge and hip mortar.</strong> Cracked or missing mortar and loose ridge caps
                let wind-driven rain in at the top of the roof and become airborne in a monsoon.
              </li>
              <li>
                <strong>Bird stops and debris.</strong> Missing bird stops at the eave invite nesting
                and pack the tile pans with material that holds water against the underlayment.
              </li>
            </ul>
            <p>
              If your roof is currently leaking and you don't know from what, start with{" "}
              <Link to="/roof-leak-repair" className={A}>
                roof leak repair
              </Link>{" "}
              — the diagnosis is the same and we'll tell you whether it's a tile issue.
            </p>
          </>
        ),
      }}
      climate={{
        heading: "Concrete, clay, and sand-cast — and why Phoenix is hard on all three",
        body: (
          <>
            <p>
              The Valley is tile country. Most homes built here from the 1980s on carry concrete
              tile; older and higher-end homes carry clay. The tile handles our climate beautifully.
              Everything underneath it does not.
            </p>
            <ul>
              <li>
                <strong>Concrete tile:</strong> heavy, durable, widely available in matching profiles.
                Chips and cracks under foot traffic; color fades but performance doesn't.
              </li>
              <li>
                <strong>Clay tile:</strong> longer-lived and more brittle. Two-piece mission and
                S-tile profiles need a careful hand — clay breaks where concrete just chips.
              </li>
              <li>
                <strong>Sand-cast and lightweight tile:</strong> found on some 1970s–80s homes.
                Fragile and often discontinued, so matching is the hard part of any repair.
              </li>
            </ul>
            <p>
              What our climate does: relentless UV and 40–50°F daily swings bake and cycle the
              underlayment until it can't hold a seal, monsoon microbursts lift and shift tile, and
              the first heavy rain after a dry summer finds every place a seal has opened. That's the
              pattern behind most of the tile calls we take in July and August.
            </p>
          </>
        ),
      }}
      process={{
        heading: "How we repair a tile roof",
        intro:
          "Tile repair is as much about the removal and reset as the fix itself. Done carelessly, a repair breaks more tile than it replaces.",
        steps: [
          {
            title: "Find the actual entry point",
            body: "Water travels along the underlayment and down the deck before it drips inside, so the stain on your ceiling is rarely under the leak. We trace it on the roof — valleys, penetrations, headwalls, and fastener lines — and photograph what we find.",
          },
          {
            title: "Lift and stack tile properly",
            body: "Tile is removed in courses and stacked on the roof so it can go back exactly where it came from. This is where inexperienced crews create new leaks and a pile of broken tile.",
          },
          {
            title: "Repair the waterproofing, not just the surface",
            body: "New underlayment in the work area, new valley metal or flashing where it's corroded, proper laps and turn-ups at walls. This is the part that actually stops the leak.",
          },
          {
            title: "Reset with matched tile and correct fastening",
            body: "Original tile goes back on visible slopes; matched replacements fill in for what's broken. Ridge and hip mortar is repointed, bird stops are replaced, and the roof is left clean.",
          },
          {
            title: "Tell you the truth about what's left",
            body: "You get photos, a written scope, and an honest read on how much life the rest of the underlayment has — so you're not surprised by another leak two slopes over next monsoon.",
          },
        ],
      }}
      extraSection={{
        heading: "Repair, underlayment replacement, or foam conversion?",
        body: (
          <>
            <p>
              Three honest options, and the right one depends on the age of the underlayment and how
              many places the roof has leaked.
            </p>
            <ul>
              <li>
                <strong>Spot repair</strong> — the answer for localized damage on a roof whose
                underlayment still has years in it. Broken tile, one valley, a flashing, a section of
                membrane.
              </li>
              <li>
                <strong>Underlayment replacement (tile R&amp;R)</strong> — for a 20+ year old roof
                with leaks in multiple areas. Your existing tile is removed, new underlayment goes
                down across the roof, and the same tile is re-installed. Costs a fraction of new tile
                because the tile is reused. See{" "}
                <Link to="/tile-roof-replacement-cost-in-phoenix" className={A}>
                  tile roof R&amp;R cost in Phoenix
                </Link>
                , or the broader{" "}
                <Link to="/tile-roofing" className={A}>
                  tile roofing service page
                </Link>
                .
              </li>
              <li>
                <strong>Tile-to-foam conversion</strong> — some Valley homeowners with low-slope tile
                take the opportunity to switch to{" "}
                <Link to="/foam-roofing" className={A}>
                  spray foam roofing
                </Link>{" "}
                for the energy savings and to remove underlayment as a failure mode entirely. We'll
                quote both if you're weighing it.
              </li>
            </ul>
            <p>
              On cost: a few broken tiles is a service call, while a valley rebuild or an underlayment
              section is a real project. Rather than guess from a webpage, we'd rather look at it —
              the inspection is free, includes photos, and ends with a written number. If the roof
              turns out to need more than you planned, give us a call and we'll talk through
              solutions; see{" "}
              <Link to="/financing" className={A}>
                cost solutions
              </Link>
              .
            </p>
          </>
        ),
      }}
      whyChoose={{
        heading: "Why homeowners call us for tile",
        body: (
          <>
            <ul>
              <li>
                <strong>Family-owned since 1989</strong> and licensed ROC223900 — the same crew names
                show up on Valley tile roofs decade after decade.
              </li>
              <li>
                <strong>We repair when repair is right.</strong> A lot of companies quote a full
                re-roof on every tile leak. We'll tell you when four tiles and a flashing are all you
                need.
              </li>
              <li>
                <strong>Profile matching is part of the job</strong>, not an afterthought. Your roof
                should look untouched when we leave.
              </li>
              <li>
                <strong>Written workmanship warranty</strong> on every repair, and photos of the work
                before the tile goes back on.
              </li>
              <li>
                <strong>Foam and tile under one roof.</strong> If a conversion is the smarter play, we
                can price it — we're not steering you toward the only system we install.
              </li>
            </ul>
            <p>
              Not sure what your roof needs yet? Book a{" "}
              <Link to="/free-roof-inspection-phoenix" className={A}>
                free roof inspection
              </Link>{" "}
              and we'll send photos and a plan.
            </p>
          </>
        ),
      }}
      faqs={faqs}
      ctaLabel="Book a Tile Roof Inspection"
      ctaTitle="Cracked tile or a leak you can't place?"
      ctaBody="We'll inspect the roof, photograph what's actually wrong, and give you a written repair price — free, with no pressure."
    />
  );
}
